<script setup lang="ts">
/**
 * 源站 .frosted-modal + .fd-reward：首存将过期。
 * 450：面板 426×478.2，关钮 33.6，星/点/倒计时抄源站模板。
 */
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { listActivityPopups } from '@/features/activity/activity-api';
import { t } from '@/shared/i18n/messages';
import { isAuthError } from '@/shared/api/auth-failure';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import { formatOroMxMoney } from '@/shared/theme/oro-mx-money';
import { useSessionStore } from '@/stores/session-store';

const STORAGE_KEY = 'oro-mx-first-deposit-dismissed';
const FIRST_DEPOSIT_MINUTES = 5;
const DEFAULT_MAX_MINOR = 300_000;
const router = useRouter();
const sessionStore = useSessionStore();
const open = ref(false);
const amountLabel = ref('$3000');
const remain = ref({ minutes: '05', seconds: '00' });

const dots = [
  { id: 0, left: '2%', top: '6%', size: 4 },
  { id: 1, left: '10%', top: '78%', size: 9 },
  { id: 2, left: '22%', top: '-4%', size: 5 },
  { id: 3, left: '38%', top: '92%', size: 3 },
  { id: 4, left: '58%', top: '-8%', size: 11 },
  { id: 5, left: '72%', top: '88%', size: 6 },
  { id: 6, left: '88%', top: '10%', size: 8 },
  { id: 7, left: '96%', top: '70%', size: 4 },
  { id: 8, left: '-2%', top: '42%', size: 7 },
  { id: 9, left: '102%', top: '38%', size: 5 },
].map((dot, index) => ({
  id: dot.id,
  style: {
    left: dot.left,
    top: dot.top,
    width: `${dot.size}px`,
    height: `${dot.size}px`,
    animationDelay: `${(index % 6) * 0.28}s`,
    animationDuration: `${2.2 + (index % 5) * 0.45}s`,
  },
}));

let deadlineMs = 0;
let timer: number | null = null;

function pad2(value: number) {
  return String(Math.max(0, Math.floor(value))).padStart(2, '0');
}

function tick() {
  const left = Math.max(0, deadlineMs - Date.now());
  const total = Math.floor(left / 1000);
  remain.value = {
    minutes: pad2(Math.floor(total / 60)),
    seconds: pad2(total % 60),
  };
}

function formatAmount(maxRewardMinor: number | null) {
  const minor = maxRewardMinor != null && maxRewardMinor > 0 ? maxRewardMinor : DEFAULT_MAX_MINOR;
  return formatOroMxMoney(minor);
}

async function loadPopup() {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STORAGE_KEY) === '1') {
    open.value = false;
    return;
  }
  try {
    const popups = await listActivityPopups(sessionStore.token || undefined, 'HOME');
    const item = popups.find((popup) => popup.templateCode === 'FIRST_DEPOSIT');
    if (!item || item.myStatus === 'CLAIMED') {
      open.value = false;
      return;
    }
    amountLabel.value = formatAmount(item.maxRewardMinor);
    open.value = true;
  } catch (error) {
    if (isAuthError(error)) {
      open.value = false;
      return;
    }
    open.value = false;
  }
}

watch(
  () => sessionStore.token,
  () => {
    void loadPopup();
  },
);

onMounted(() => {
  deadlineMs = Date.now() + FIRST_DEPOSIT_MINUTES * 60 * 1000;
  tick();
  timer = window.setInterval(tick, 1000);
  void loadPopup();
});

onUnmounted(() => {
  if (timer != null) window.clearInterval(timer);
});

function close() {
  open.value = false;
  sessionStorage.setItem(STORAGE_KEY, '1');
}

function goDeposit() {
  close();
  void router.push('/deposit');
}
</script>

<template>
  <div v-if="open" class="frosted-modal">
    <button class="frosted-modal__backdrop" type="button" :aria-label="t('oro.common.close')" @click="close" />
    <div class="frosted-modal__panel app-modal-host__first-deposit-panel" role="dialog" aria-modal="true">
      <div class="fd-reward">
        <div class="fd-reward__title-wrap">
          <span
            v-for="dot in dots"
            :key="dot.id"
            class="fd-reward__dot"
            :style="dot.style"
            aria-hidden="true"
          />
          <img
            class="fd-reward__star fd-reward__star--sm"
            :src="oroMxAssetPaths.firstDepositStar"
            alt=""
            aria-hidden="true"
          />
          <h2 class="fd-reward__title">{{ t('oro.fd.title') }}</h2>
          <img
            class="fd-reward__star fd-reward__star--lg"
            :src="oroMxAssetPaths.firstDepositStar"
            alt=""
            aria-hidden="true"
          />
        </div>
        <button
          class="fd-reward__close"
          type="button"
          aria-label="close"
          :style="{ backgroundImage: `url(${oroMxAssetPaths.firstDepositClose})` }"
          @click="close"
        />
        <div class="fd-reward__shell">
          <img
            class="fd-reward__ribbon fd-reward__ribbon--left"
            :src="oroMxAssetPaths.firstDepositRibbonLeft"
            alt=""
          />
          <img
            class="fd-reward__ribbon fd-reward__ribbon--right"
            :src="oroMxAssetPaths.firstDepositRibbonRight"
            alt=""
          />
          <div class="fd-reward__panel">
            <div class="fd-reward__card">
              <span class="fd-reward__card-notch fd-reward__card-notch--left" />
              <span class="fd-reward__amount">{{ amountLabel }}</span>
              <span class="fd-reward__card-notch fd-reward__card-notch--right" />
            </div>
            <div class="fd-reward__countdown">
              <span class="fd-reward__countdown-label">{{ t('oro.fd.remain') }}</span>
              <span class="fd-reward__countdown-digit">{{ remain.minutes }}</span>
              <span class="fd-reward__countdown-sep">:</span>
              <span class="fd-reward__countdown-digit">{{ remain.seconds }}</span>
            </div>
            <button class="fd-reward__btn" type="button" @click="goDeposit">
              <span class="fd-reward__btn-label">{{ t('oro.fd.go') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.frosted-modal {
  position: fixed;
  inset: 0;
  z-index: 3500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4267rem;
}

.frosted-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgb(0 0 0 / 42%);
  backdrop-filter: blur(16px) saturate(1.2);
  border: 0;
}

.frosted-modal__panel {
  position: relative;
  z-index: 1;
  width: 18.9333rem;
}

.fd-reward {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  overflow: visible;
  animation: fd-reward-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.fd-reward__title-wrap {
  position: relative;
  display: flex;
  gap: 0.32rem;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  max-width: 19.2rem;
  margin-bottom: 1.9733rem;
  padding: 0 1.28rem;
  overflow: visible;
}

.fd-reward__title {
  position: relative;
  z-index: 2;
  max-width: 14.9333rem;
  margin: 0;
  color: transparent;
  font-size: 1.28rem;
  font-weight: 400;
  line-height: 1.25;
  text-align: center;
  background: linear-gradient(90.28deg, #ffd8a1 0.2%, #fff3dc 42.27%, #f5c277 96.35%);
  background-clip: text;
  filter: drop-shadow(rgb(80 40 0 / 35%) 0 0.1067rem 0.2133rem);
  -webkit-text-fill-color: transparent;
}

.fd-reward__star {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  object-fit: contain;
  animation: fd-star-twinkle 1.8s ease-in-out infinite;
}

.fd-reward__star--sm {
  align-self: center;
  width: 0.96rem;
  height: 0.96rem;
  margin-top: 0.7467rem;
  animation-delay: 0.2s;
}

.fd-reward__star--lg {
  align-self: center;
  width: 1.2rem;
  height: 1.2rem;
  margin-top: -0.48rem;
  animation-delay: 0.7s;
}

.fd-reward__dot {
  position: absolute;
  z-index: 0;
  background: #fff9c5;
  border-radius: 50%;
  box-shadow: rgb(255 249 197 / 65%) 0 0 0.16rem;
  pointer-events: none;
  animation: fd-dot-float 2.6s ease-in-out infinite;
}

.fd-reward__close {
  position: absolute;
  top: -2.88rem;
  right: max(0px, 50% - 8.32rem);
  z-index: 8;
  width: 1.4933rem;
  height: 1.4933rem;
  padding: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.fd-reward__shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 15.1467rem;
  height: 14.48rem;
  margin: 0 auto;
  overflow: visible;
  background: linear-gradient(135.61deg, #fdca8c 6.15%, #f2dec7 52.18%, #db9b3b 96.47%);
  border-radius: 1.3333rem;
}

.fd-reward__ribbon {
  position: absolute;
  z-index: 3;
  object-fit: contain;
  pointer-events: none;
}

.fd-reward__ribbon--left {
  bottom: 2.0267rem;
  left: -2.5867rem;
  width: 6.24rem;
  height: 7.3333rem;
  transform-origin: 92% 45%;
  animation: fd-ribbon-sway-left 3.2s ease-in-out infinite;
}

.fd-reward__ribbon--right {
  top: -0.5333rem;
  right: -1.3867rem;
  width: 4.6133rem;
  height: 8.1067rem;
  transform-origin: 8% 35%;
  animation: fd-ribbon-sway-right 3.6s ease-in-out infinite;
}

.fd-reward__panel {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 14.8267rem;
  height: 14.0533rem;
  padding: 1.92rem 0.7467rem 0.96rem;
  overflow: hidden;
  background: radial-gradient(93.66% 93.66% at 50% 6.34%, #6b445b, #462f62);
  border-radius: 1.28rem;
}

.fd-reward__card {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 11.7067rem;
  height: 4.5333rem;
  overflow: visible;
  background: linear-gradient(90deg, #ffefd4 3.08%, #fff8e6 28.07%, #ffefcd 95.9%);
  border-radius: 0.48rem;
  box-shadow: rgb(0 0 0 / 22%) 0 0.2667rem 0.64rem;
}

.fd-reward__card-notch {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 0.8rem;
  height: 0.8rem;
  background: #462f62;
  border-radius: 50%;
  transform: translateY(-50%);
}

.fd-reward__card-notch--left {
  left: -0.4rem;
}

.fd-reward__card-notch--right {
  right: -0.4rem;
}

.fd-reward__amount {
  margin: 0;
  color: #4e1f00;
  font-size: 1.92rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
}

.fd-reward__countdown {
  display: flex;
  gap: 0.32rem;
  align-items: center;
  justify-content: center;
  margin-top: 0.96rem;
}

.fd-reward__countdown-label {
  color: #fefde6;
  font-size: 0.7467rem;
  font-weight: 600;
}

.fd-reward__countdown-digit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1467rem;
  height: 1.1467rem;
  color: #6c2d04;
  font-size: 0.6933rem;
  font-weight: 800;
  line-height: 1;
  background: #fadaa4;
  border-radius: 0.2133rem;
  box-shadow: rgb(255 255 255 / 45%) 0 1px inset;
}

.fd-reward__countdown-sep {
  color: #fefde6;
  font-size: 0.7467rem;
  font-weight: 700;
  line-height: 1;
}

.fd-reward__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 10.16rem;
  height: 2.56rem;
  margin-top: 1.4933rem;
  padding: 0;
  background: linear-gradient(#fee4a0, #ffc579);
  border: 0.16rem solid #fee4a0;
  border-radius: 1.3333rem;
  box-shadow: #f38220 0 0 0 0.16rem;
  cursor: pointer;
  animation: fd-btn-breathe 1.8s ease-in-out infinite;
}

.fd-reward__btn-label {
  color: transparent;
  font-size: 0.96rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(#2d1c18 33.33%, #865347 84.44%);
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

@keyframes fd-reward-in {
  0% {
    opacity: 0;
    transform: translateY(0.48rem) scale(0.96);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fd-dot-float {
  0%,
  100% {
    opacity: 0.4;
    transform: translateY(0) scale(0.92);
  }

  50% {
    opacity: 0.95;
    transform: translateY(-0.1333rem) scale(1.08);
  }
}

@keyframes fd-star-twinkle {
  0%,
  100% {
    filter: drop-shadow(rgb(255 249 197 / 0%) 0 0 0);
    transform: scale(1) rotate(0deg);
  }

  50% {
    filter: drop-shadow(rgb(255 249 197 / 85%) 0 0 0.2667rem);
    transform: scale(1.12) rotate(8deg);
  }
}

@keyframes fd-ribbon-sway-left {
  0%,
  100% {
    transform: rotate(-1.2deg);
  }

  50% {
    transform: rotate(1.4deg);
  }
}

@keyframes fd-ribbon-sway-right {
  0%,
  100% {
    transform: rotate(1.2deg);
  }

  50% {
    transform: rotate(-1.4deg);
  }
}

@keyframes fd-btn-breathe {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.03);
  }
}
</style>
